<template>
  <div class="control-tile" :class="toneClass">
    <span class="control-tile-label">
      <ControlIcon :name="icon" size="1.15em" />
      <span class="control-tile-label-text"
        ><slot name="label">{{ label }}</slot></span
      >
    </span>
    <span class="control-tile-value">
      <slot>{{ value }}</slot>
      <small v-if="unit" class="control-tile-unit">{{ unit }}</small>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';

const props = withDefaults(
  defineProps<{
    /** A ControlIcon name — never a Material glyph. */
    icon: string;
    label: string;
    /** The reading (`61`, `34`, `12:40`) — the unit rides beside it, smaller. */
    value: string;
    unit?: string;
    /** `accent` paints the reading in the sheet's colour, `muted` calms it. */
    tone?: 'accent' | 'muted';
  }>(),
  { unit: '', tone: undefined },
);

const toneClass = computed(() => (props.tone ? `control-tile--${props.tone}` : null));
</script>

<style scoped>
.control-tile {
  /* The sheet's accent, or the light accent when nothing has set one. */
  --tile-accent: var(--sheet-accent, var(--device-color-light));
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  padding: calc(0.625rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.75rem * var(--ui-radius-factor));
  background: rgba(148, 163, 184, 0.1);
}

.dark .control-tile {
  background: rgba(255, 255, 255, 0.04);
}

.control-tile-label {
  display: inline-flex;
  align-items: center;
  gap: 0.375em;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
  min-width: 0;
}

.dark .control-tile-label {
  color: rgb(var(--gray-400));
}

.control-tile-label-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.control-tile-value {
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.25;
  font-variant-numeric: tabular-nums;
}

.control-tile-unit {
  margin-left: 0.2em;
  font-size: 0.6875rem;
  font-weight: 600;
  color: rgb(var(--gray-500));
}

.dark .control-tile-unit {
  color: rgb(var(--gray-400));
}

.control-tile--accent .control-tile-value {
  color: var(--tile-accent);
}

.control-tile--muted .control-tile-value {
  color: rgb(var(--gray-500));
}

.dark .control-tile--muted .control-tile-value {
  color: rgb(var(--gray-400));
}
</style>
